import React, { useEffect, useState } from "react";
import { Input, Button, Modal, Select, Radio, Alert, message } from "antd";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import ButtonLoader from "../ButtonLoader";
import TextArea from "antd/es/input/TextArea";
import {
  getLocalStorageJSONData,
  setLocalStorageJSONData,
} from "../../utils/Misc";
import { set } from "lodash";

const { Option } = Select;

const schema = yup.object().shape({
  name: yup.string().required("Screen name is required"),
  playlist: yup.string().required("Playlist is required"),
  status: yup.string().required("Status is required"),
});

const ScreenForm = ({
  isModalOpen,
  editingScreen,
  closeModal,
  handleUpdateList,
}) => {
  const [loading, setLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsMessage, setErrorsMessage] = useState("");
  const [slugValue, setSlugValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isDataSet, setIsDataSet] = useState(false);
  const [playlistOptions, setPlaylistOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [passcodeOptions, setPasscodeOptions] = useState([]);
  const [userAllowedLocations, setUserAllowedLocations] = useState([]);
  const [regeneratePasscode, setRegeneratePasscode] = useState("yes");

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit: !!editingScreen },
    defaultValues: {
      status: "active",
      regenerate_passcode: "yes",
      orientation: "landscape",
    },
  });

  const nameValue = watch("name");

  useEffect(() => {
    if (nameValue && !editingScreen) {
      const slug = nameValue
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "") // remove special chars
        .replace(/\s+/g, "-") // replace spaces with dashes
        .replace(/-+/g, "-"); // remove duplicate dashes
      setValue("slug", slug);
      setSlugValue(slug);
    }
  }, [nameValue, setValue]);

  const getDistinctCities = (locations) => {
    const cityMap = new Map();

    locations.forEach((location) => {
      if (location.city_id && location.city_name && !cityMap.has(location.city_id)) {
        cityMap.set(location.city_id, {
          value: location.city_id,
          label: location.city_name,
        });
      }
    });

    return Array.from(cityMap.values());
  };

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/screens/masters`
      );

      const status = response.data.data.status;
      const passcode= response.data.data.passcode;
      const playlists = response.data.data.playlists;
      const masterLocations = response.data.data.locations;
      console.log(masterLocations);

      setStatusOptions(status);
      setPasscodeOptions(passcode);
      setPlaylistOptions(
        playlists.map((playlist) => {
          return {
            value: playlist.id,
            label: playlist.title,
          };
        })
      );

      const userData = getLocalStorageJSONData("userData");
      let allowedLocations = [];

      if (userData?.locations && userData.locations.length > 0) {
        allowedLocations = masterLocations.filter((loc) =>
          userData.locations.includes(loc.id)
        );
      } else {
        allowedLocations = masterLocations;
      }

      setLocalStorageJSONData("locations", allowedLocations);
      setUserAllowedLocations(allowedLocations);

      const cityOptionsFromUserLocations = getDistinctCities(allowedLocations);
      setCityOptions(cityOptionsFromUserLocations);

      setLoading(false);
    } catch (err) {
      console.error(err.message);
      setLoading(false);
    }
  };

  const fetchScreen = async (screenId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/screens/${screenId}`
      );

      const screenData = response.data.data;
      setRegeneratePasscode(screenData.regenerate_passcode);
      const selectedPlaylist = playlistOptions.find(
        (playlist) => playlist.value === screenData.playlist_id
      );

      const selectedCity = cityOptions.find(
        (city) => city.value === screenData.city_id
      ) || null;

      // Filter locations for the selected city from user's allowed locations
      const filteredLocations = userAllowedLocations
        .filter((location) => location.city_id === selectedCity?.value)
        .map((location) => {
          return {
            value: location?.id,
            label: (
              <>
                {location?.name}{" "}
                <span style={{ color: "#888" }}>
                  ({location?.code && location.code.toUpperCase()})
                </span>
              </>
            ),
          };
        });

      const selectedLocation = filteredLocations.find(
        (location) => location.value === screenData.location_id
      );

      const editFormData = {
        ...screenData,
        playlist: selectedPlaylist ? screenData.playlist_id : null,
        city: selectedCity ? screenData.city_id : null,
        location: selectedLocation ? screenData.location_id : null,
      };

      setLocationOptions(filteredLocations);
      setSlugValue(editFormData.slug);
      reset(editFormData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching screen:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDataAndSetState = async () => {
      await fetchMasterData();
      setIsDataSet(true);
    };
    fetchDataAndSetState();
  }, []);

  useEffect(() => {
    if (isDataSet && editingScreen) {
      fetchScreen(editingScreen.id);
    }
  }, [isDataSet, editingScreen, playlistOptions, cityOptions]);

  const onSubmitScreen = async (data) => {
    let differFormFields = {};
    if (editingScreen !== null) {
      differFormFields = {
        id: editingScreen.id,
      };
    }
    const finalFormData = {
      name: data.name.trim(),
      slug: data.slug.trim(),
      description: (data.description && data.description.trim()) || "",
      city_id: data.city,
      location_id: data.location,
      floor: (data.floor && data.floor.trim()) || "",
      orientation: data.orientation,
      playlist_id: data.playlist,
      status: data.status,
      regenerate_passcode: regeneratePasscode,
      ...differFormFields,
    };
    setSubmitting(true);
    let newFormData = finalFormData;
    const url =
      editingScreen === null
        ? `${process.env.REACT_APP_BASE_URL}/api/screens/create`
        : `${process.env.REACT_APP_BASE_URL}/api/screens/update`;

    try {
      if (editingScreen === null) {
        await axios.post(url, newFormData);
        message.success("Screen created successfully");
      } else {
        const response = await axios.put(url, newFormData);
        const updated_row_count = response.data.data.updated_row_count;
        if (updated_row_count === 1) {
          message.success("Screen updated successfully");
        }
      }
      setSubmitting(false);
      setShowErrors(false);
      handleUpdateList();
      closeModal();
    } catch (error) {
      if (error.response && error.response.status > 300) {
        const errorData = error.response.data.message;
        console.log(errorData);
        setErrorsMessage(errorData);
        setShowErrors(true);
        setTimeout(() => {
          setShowErrors(false);
        }, 3000);
        setSubmitting(false);
      } else {
        console.error("Error:", error);
      }
    }
  };

  const handleCityChange = (value) => {
    setValue("city", value ?? null, { shouldDirty: true });
    setValue("location", null, { shouldDirty: true });

    if (!value) {
      setLocationOptions([]);
      return;
    }

    const filteredLocations = userAllowedLocations
      .filter((location) => location?.city_id === value)
      .map((location) => ({
        value: location?.id,
        label: (
          <>
            {location?.name}{" "}
            <span style={{ color: "#888" }}>
              ({location?.code ? location.code.toUpperCase() : ""})
            </span>
          </>
        ),
      }));

    setLocationOptions(filteredLocations);
  };

  return (
    <Modal
      title={editingScreen ? "Edit Screen" : "Add New Screen"}
      open={isModalOpen}
      onCancel={() => {
        reset();
        closeModal();
      }}
      footer={null}
      maskClosable={false}
    >
      {loading ? (
        <ButtonLoader wrapperClass={`min-h-50`} />
      ) : (
        <>
          {showErrors && (
            <>
              <div className="col-sm-12 mb-3">
                <Alert message={errorsMessage} type="error" closeIcon={false} />
              </div>
            </>
          )}
          <form onSubmit={handleSubmit(onSubmitScreen)}>
            <div className="mb-3">
              <label className="form-label">
                Screen Name <span className="text-danger">*</span>
              </label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.name && (
                <div className="text-danger small">{errors.name.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Slug</label>
              <Controller
                name="slug"
                control={control}
                render={({ field }) => (
                  <Input {...field} readOnly={true} value={slugValue} />
                )}
              />
              {errors.slug && (
                <div className="text-danger small">{errors.slug.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => <TextArea {...field} />}
              />
              {errors.description && (
                <div className="text-danger small">
                  {errors.description.message}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">City</label>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    onChange={handleCityChange}
                    allowClear
                    placeholder="Select a city"
                  >
                    {cityOptions.map((city) => (
                      <Option value={city.value} key={city.value}>
                        {city.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.city && (
                <div className="text-danger small">{errors.city.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Location</label>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    allowClear
                    showSearch
                    placeholder="Select a location"
                    filterOption={(input, option) => {
                      return (option?.children?.props?.children[0] ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }}
                    notFoundContent="Please select city to see its locations"
                  >
                    {locationOptions.map((location) => (
                      <Option value={location.value} key={location.value}>
                        {location.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.location && (
                <div className="text-danger small">
                  {errors.location.message}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Floor</label>
              <Controller
                name="floor"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.floor && (
                <div className="text-danger small">{errors.floor.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Orientation</label>
              <Controller
                name="orientation"
                control={control}
                render={({ field }) => (
                  <Radio.Group {...field}>
                    <Radio value="landscape">Landscape</Radio>
                    <Radio value="portrait">Portrait</Radio>
                  </Radio.Group>
                )}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">
                Playlist <span className="text-danger">*</span>
              </label>
              <Controller
                name="playlist"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    showSearch
                    placeholder="Select a playlist"
                    filterOption={(input, option) => {
                      return (option?.children ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }}
                    style={{ width: "100%" }}
                    onChange={field.onChange}
                  >
                    {playlistOptions.map((playlist) => (
                      <Option value={playlist.value} key={playlist.value}>
                        {playlist.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.playlist && (
                <div className="text-danger small">
                  {errors.playlist.message}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label me-3">Status</label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Radio.Group {...field}>
                    {statusOptions.map((status) => (
                      <Radio value={status.value} key={status.value}>
                        {status.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                )}
              />
              {errors.status && (
                <div className="text-danger small">{errors.status.message}</div>
              )}
            </div>
            {/* <div className="mb-3">
              <label className="form-label me-3">Regenerate Passcode</label>
              <Controller
                name="regenerate_passcode"
                control={control}
                render={({ field }) => (
                  <Radio.Group {...field}>
                    {passcodeOptions.map((passcode) => (
                      <Radio value={passcode.value} key={passcode.value}>
                        {passcode.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                )}
              />
              {errors.regenerate_passcode && (
                <div className="text-danger small">{errors.regenerate_passcode.message}</div>
              )}
            </div> */}

            <Button
              type="primary"
              htmlType="submit"
              block
              disabled={submitting}
            >
              {!submitting ? (
                editingScreen ? (
                  "Update Screen"
                ) : (
                  "Add Screen"
                )
              ) : (
                <ButtonLoader text="Submitting..." />
              )}
            </Button>
          </form>
        </>
      )}
    </Modal>
  );
};

export default ScreenForm;
